/**
 * Billing scenario constants for the admin Product Setup UI.
 *
 * NOTE: These mirror the `BillingScenario` enum and tier eligibility exposed by the API
 * (billing pricing-scenarios spec). They are defined locally so the SPA can render scenario
 * selection / coverage; once `npm run gen:api` is run against an API that includes the
 * scenario fields, the generated `BillingScenario` type can be used instead.
 */

/** All billing scenarios, in display order. */
export const BILLING_SCENARIOS = [
  'StandardMonthly',
  'StandardAnnual',
  'NonProfitAnnual',
  'PartnerSourcedAnnual',
  'PartnerAddOn',
] as const;

/** A billing scenario identifier. */
export type BillingScenario = (typeof BILLING_SCENARIOS)[number];

/** Human-friendly labels for each scenario. */
export const BILLING_SCENARIO_LABELS: Record<BillingScenario, string> = {
  StandardMonthly: 'Standard — Monthly',
  StandardAnnual: 'Standard — Annual',
  NonProfitAnnual: 'Non-profit — Annual',
  PartnerSourcedAnnual: 'Partner-sourced — Annual',
  PartnerAddOn: 'Partner add-on',
};

/** Eligibility tiers, in precedence order. */
export const BILLING_TIERS = ['Non-profit', 'Partner', 'Partner-sourced', 'Standard'] as const;

/** A billing eligibility tier. */
export type BillingTier = (typeof BILLING_TIERS)[number];

/** The eligibility tier each scenario belongs to. */
export const SCENARIO_TIER: Record<BillingScenario, BillingTier> = {
  StandardMonthly: 'Standard',
  StandardAnnual: 'Standard',
  NonProfitAnnual: 'Non-profit',
  PartnerSourcedAnnual: 'Partner-sourced',
  PartnerAddOn: 'Partner',
};

/** The scenarios offered to each tier (mirrors the server-side eligibility mapping). */
export const TIER_SCENARIOS: Record<BillingTier, readonly BillingScenario[]> = {
  'Non-profit': ['NonProfitAnnual'],
  Partner: ['PartnerAddOn'],
  'Partner-sourced': ['PartnerSourcedAnnual'],
  Standard: ['StandardMonthly', 'StandardAnnual'],
};

/**
 * Resolves the billing scenarios an organization is eligible for from its pricing-tier flags.
 *
 * Mirrors the server-side `BillingScenarioPolicy.ResolveEligibleScenarios`: exactly one tier is
 * selected by precedence (non-profit → partner → partner-sourced → standard). The enforced flag
 * invariants keep non-profit/partner and partner/source-partner mutually exclusive.
 */
export function resolveEligibleScenarios(
  isNonProfit: boolean,
  isPartner: boolean,
  hasSourcePartner: boolean
): readonly BillingScenario[] {
  if (isNonProfit) {
    return TIER_SCENARIOS['Non-profit'];
  }

  if (isPartner) {
    return TIER_SCENARIOS.Partner;
  }

  if (hasSourcePartner) {
    return TIER_SCENARIOS['Partner-sourced'];
  }

  return TIER_SCENARIOS.Standard;
}
